import { adminRequest } from "./client";

/**
 * KMS-material teardown status of a session key, from the admin endpoint
 * `POST /wallets/sessionKeys/kmsStatuses`. Combines the Spanner row (`state`,
 * `kmsKeyDestroyedAt`) with the live KMS `cryptoKeyVersion` status. This is fetched on demand
 * (not a Firestore projection) precisely so none of these KMS internals land in the
 * owner-readable mobile `sessionKeys` doc.
 */
export interface KmsStatusDto {
  id: string;
  type: string;
  state: string; // pending | enabled | revoked
  /** When teardown was scheduled (Spanner). Null = not torn down. */
  kmsKeyDestroyedAt: string | null;
  /** Live KMS version state: ENABLED | DISABLED | DESTROY_SCHEDULED | DESTROYED | null. */
  kmsVersionState: string | null;
  /** Live KMS: when the material is irreversibly destroyed (restorable until then). */
  kmsDestroyTime: string | null;
}

function fetchKmsStatuses(ids: string[], token: string): Promise<{ statuses: KmsStatusDto[] }> {
  return adminRequest({
    method: "POST",
    path: "/wallets/sessionKeys/kmsStatuses",
    body: { ids },
    token,
  });
}

// ── Batched loader ──────────────────────────────────────────────────────────
// All `loadKmsStatus` calls within a ~40ms window collapse into one request, so a list of N
// session-key cells fires a single `kmsStatuses` POST. Successful statuses are cached for the
// session (KMS status is near-static — set once at teardown); errors are not cached so they retry.

const cache = new Map<string, KmsStatusDto | null>();
let pendingIds = new Set<string>();
let pendingResolvers = new Map<string, ((s: KmsStatusDto | null) => void)[]>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let latestGetToken: (() => Promise<string>) | null = null;

async function flush(): Promise<void> {
  flushTimer = null;
  const ids = [...pendingIds];
  const resolvers = pendingResolvers;
  const getToken = latestGetToken;
  pendingIds = new Set();
  pendingResolvers = new Map();

  const resolve = (id: string, status: KmsStatusDto | null) =>
    (resolvers.get(id) ?? []).forEach((r) => r(status));

  try {
    if (!getToken) throw new Error("No auth token getter registered.");
    const token = await getToken();
    const { statuses } = await fetchKmsStatuses(ids, token);
    const byId = new Map(statuses.map((s) => [s.id, s] as const));
    for (const id of ids) {
      const status = byId.get(id) ?? null;
      cache.set(id, status); // cache hits + genuine misses (key has no status)
      resolve(id, status);
    }
  } catch {
    // Transient failure — don't cache, so the next render retries.
    for (const id of ids) resolve(id, null);
  }
}

export function loadKmsStatus(
  id: string,
  getToken: () => Promise<string>,
): Promise<KmsStatusDto | null> {
  if (cache.has(id)) return Promise.resolve(cache.get(id) ?? null);
  latestGetToken = getToken;
  return new Promise((resolve) => {
    pendingIds.add(id);
    pendingResolvers.set(id, [...(pendingResolvers.get(id) ?? []), resolve]);
    if (!flushTimer) flushTimer = setTimeout(() => void flush(), 40);
  });
}

/** Drop cached statuses — call after a teardown/revoke so the badge refetches. */
export function clearKmsStatusCache(): void {
  cache.clear();
}
